import { Card, Col, notification, Row, Select, Table, Tooltip } from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../../api/calls";
import EmployeeLink from "../../Employees/EmployeeLink";
import { useSelector } from "react-redux";
import { getAreaScope } from "../../../actions";
import { Link } from "react-router-dom";
import { fetchManagers } from "../../../api/managerApi";


const ClientClaimsReport = ({ year }) => {
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [managers, setManagers] = useState([]);
  const [statusFilters, setStatusFilters] = useState({});
  // Get city id from global filter
  const areaScope = useSelector(getAreaScope);
  const selectedCity = areaScope.areaScope;

  const [filters, setFilters] = useState({
    active_partners_only: 1,
    manager_ids: null,
  });

  const fetchData = useCallback(() => {
    if (!year || !selectedCity) return;

    const params = {
      year: year.format("YYYY-MM-DD"),
      city_branch_id: selectedCity || null,
      active_partners_only: filters?.active_partners_only,
      userIds: filters?.manager_ids || null,
    };

    setLoading(true);
    coreApi
      .get("/reports/country/client-claims", {
        params: params,
      })
      .then((res) => {
        const sorted = [...res.data].sort((a, b) =>
          (a.reference || "").localeCompare(b.reference || "")
        );
        setData(sorted);
      })
      .catch((err) => notification.error({ message: err?.response?.data?.message }))
      .finally(() => {
        setLoading(false);
      });

  }, [year, selectedCity, filters]);

  const getRowClassName = (record) => {
    let overLimit = false;

    for (let i = 1; i <= 12; i++) {
      const sum =
        Number(record[`claims_count_month_${i}`] || 0) +
        Number(record[`claims_count_month_${i + 1}`] || 0) +
        Number(record[`claims_count_month_${i + 2}`] || 0);

      if (sum > 3) {
        overLimit = true;
        break;
      }
    }

    return overLimit ? "ant-tag-red" : "";
  };

  useEffect(() => {
    fetchData();
    fetchManagers().then(setManagers);
  }, [year, selectedCity, filters]);


  const columns = [
    {
      title: t("buildings.registrationNumber"),
      key: "building_registration_number",
      dataIndex: "registration_number",
      sorter: (a, b) => (a.registration_number || "").localeCompare(b.registration_number || ""),
      width: "110px",
      fixed: "left",
      render: (reg, row) => (
        <Tooltip title={reg}>
          <Link
            to={`/buildings/${row.building_id}#claims`}
            target="_blank"
            style={{ borderBottom: "1px dotted" }}
          >
            <span>{reg}</span>
          </Link>
        </Tooltip>
      ),
    },
    {
      title: t("common.address"),
      key: "reference",
      dataIndex: "reference",
      sorter: (a, b) => (a.reference || "").localeCompare(b.reference || ""),
      width: 220,
      fixed: "left",
      ellipsis: true,
      render: (_, row) => {
        const { reference, city, district } = row || {};
        if (!reference) return "-";

        const fullAddress = [reference, city, district].filter(Boolean).join(", ");

        return (
          <Tooltip title={fullAddress}>
            <span style={{
              width: 220,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }} >{reference}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t("invoices.manager"),
      key: "responsibility",
      dataIndex: "responsibility",
      width: 170,
      sorter: (a, b) => {
        const aName = a.responsibility?.employee?.first_name || "";
        const bName = b.responsibility?.employee?.first_name || "";
        return aName.localeCompare(bName);
      },
      filteredValue: filters.manager_ids || null,
      render: (responsibility) =>
        responsibility ? <EmployeeLink employee={responsibility.employee} /> : "-",
      filterDropdown: ({ confirm }) => (
        <div style={{ padding: 8, width: 250 }}>
          <Select
            mode="multiple"
            allowClear
            style={{ width: 220 }}
            placeholder={t("managers.selectManager")}
            value={filters.manager_ids || []}
            onChange={(value) => {
              const newFilters = { ...filters, manager_ids: value.length ? value : null };
              setFilters(newFilters);
              confirm();
            }}
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            optionLabelProp="label"
          >
            {managers.map((manager) => (
              <Select.Option
                key={manager.user?.id}
                value={manager.user?.id}
                label={`${manager.first_name} ${manager.last_name}`}
              >
                {manager.user?.deleted_at === null
                  ? `${manager.first_name} ${manager.last_name}`
                  : <s>{`${manager.first_name} ${manager.last_name}`}</s>}
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
    },
    ...Array.from({ length: 12 }, (_, i) => ({
      title: moment().month(i).format("MMMM"),
      children: [
        {
          title: t("reports.country.claimCount"),
          key: `claims_count_month_${i + 1}`,
          width: 100,
          dataIndex: `claims_count_month_${i + 1}`,
          sorter: (a, b) =>
            (a[`claims_count_month_${i + 1}`] || 0) - (b[`claims_count_month_${i + 1}`] || 0),
          render: (value) => (
            <span style={{ color: value > 0 ? "red" : "black" }}>{value}</span>
          ),
        },
        {
          title: t("reports.country.claimStatus"),
          key: `last_claim_status_month_${i + 1}`,
          dataIndex: `last_claim_status_month_${i + 1}`,
          width: 100,
          filters: [
            { text: t("reports.country.inSolution"), value: "inSolution" },
            { text: t("reports.country.solved"), value: "solved" },
            { text: t("common.empty"), value: "" },
          ],
          filteredValue: statusFilters[`last_claim_status_month_${i + 1}`] || null,
          onFilter: (value, record) => {
            const cellValue = record[`last_claim_status_month_${i + 1}`] || "";
            return cellValue === value;
          },
          sorter: (a, b) =>
            (a[`last_claim_status_month_${i + 1}`] || "").localeCompare(
              b[`last_claim_status_month_${i + 1}`] || ""
            ),
          render: (value) => (
            <span style={{ color: value === "inSolution" ? "red" : "green" }}>
              {value ? t("reports.country." + value) : ""}
            </span>
          ),
        },
        {
          title: t("reports.country.dateSatisfactionCheck"),
          key: `last_satisfaction_check_month_${i + 1}`,
          dataIndex: `last_satisfaction_check_month_${i + 1}`,
          width: 100,
          sorter: (a, b) =>
            new Date(a[`last_satisfaction_check_month_${i + 1}`] || 0) -
            new Date(b[`last_satisfaction_check_month_${i + 1}`] || 0),
        },
      ],
    })),
    {
      title: t("common.total"),
      key: "claims_total",
      dataIndex: "claims_total",
      fixed: "right",
      width: 75,
      sorter: (a, b) => (a.claims_total || 0) - (b.claims_total || 0),
      render: (value) => value || 0,
    }
  ];

  const renderTableSummary = (pageData) => {
    if (pageData.length === 0) return null;

    const totalClaims = Array.from({ length: 12 }, (_, i) =>
      pageData.reduce((sum, row) => sum + Number(row[`claims_count_month_${i + 1}`] || 0), 0)
    );

    const totalSum = pageData.reduce(
      (sum, row) => sum + Number(row[`claims_total`] || 0),
      0
    );

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={3}>
          <strong>{t("common.total")}</strong>
        </Table.Summary.Cell>
        {totalClaims.map((sum, i) => (
          <Table.Summary.Cell key={i} index={i + 3} colSpan={3}>
            <strong>{sum}</strong>
          </Table.Summary.Cell>
        ))}
        <Table.Summary.Cell index={15}>
          <strong>{totalSum}</strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card size="small" bodyStyle={{ padding: "10px" }}>
          <Table
            rowKey="building_id"
            loading={isLoading}
            size="small"
            dataSource={data}
            columns={columns}
            pagination={false}
            rowClassName={getRowClassName}
            summary={renderTableSummary}
            onChange={(pagination, filters, sorter) => {
              setStatusFilters(filters);
            }}
            scroll={{ x: 2000 }}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default ClientClaimsReport;